import { parsePhoneNumber } from 'react-phone-number-input';
import isURL from 'validator/lib/isURL';

import { ZIP_CODE_MAX_LENGTH } from 'constants/address.constants';
import { PASSWORD_REQUIREMENTS } from 'constants/password.constants';
import {
  MAX_PERCENTAGE_OWNED,
  MAX_PERCENTAGE_OWNED_ERROR_MESSAGE,
  MIN_PERCENTAGE_OWNED,
  MIN_PERCENTAGE_OWNED_ERROR_MESSAGE,
} from 'components/users/users.constants';
import {
  DEPRECATED_FEDERAL_TAX_IDS,
  FEDERAL_TAX_ID_WITH_DASH_MAX_LENGTH,
} from 'components/prequalification/RegistrationProcess/registrationProcess.constants';

const ALLOWED_PHONE_NUMBER_COUNTRY_CODES = ['US', 'PR'];

export const validateRequiredField = (
  value: string | null | undefined,
  fieldName: string = 'This information',
): string => {
  if (!value || !value.trim()) {
    return `${fieldName} is required.`;
  }

  return '';
};

export const validateRequiredArrayField = (value: any, fieldName: string = 'This information'): string => {
  if (!value || !value?.length) {
    return `${fieldName} is required.`;
  }

  return '';
};

export const validateCheckboxField = (value: boolean): string => {
  if (!value) {
    return `A mark is required.`;
  }

  return '';
};

export const getMessageForInvalidFields = (fieldName: string = 'This information') => {
  return `${fieldName} is invalid`;
};

export const isValidPhone = (value?: string) => {
  const errorMessage = 'Not a valid US phone number';

  if (!value) {
    return errorMessage;
  }

  return ALLOWED_PHONE_NUMBER_COUNTRY_CODES.some((countryCode) => parsePhoneNumber(value)?.country === countryCode)
    ? ''
    : errorMessage;
};

export const validateFirstName = (value: string, fieldName: string) => {
  if (value.trim().length < 2) {
    return `${fieldName} cannot be less than 2 characters`;
  }

  const latinLettersRegex = /^[a-zA-Z]+$/;

  if (!latinLettersRegex.test(value)) {
    return `${fieldName} can only contain Latin letters`;
  }

  return '';
};

export const validateZipCode = (value: string, fieldName: string): string => {
  if (value.trim().replace(/\D/g, '').length !== ZIP_CODE_MAX_LENGTH) {
    return `${fieldName} is invalid`;
  }

  return '';
};

export const validatePassword = (password: string) => {
  const invalidValidation = PASSWORD_REQUIREMENTS.find((requirement) => !requirement.isValid(password));

  return invalidValidation?.message || '';
};

export const matchConfirmedPassword = (password: string, confirmedPassword: string) => {
  return password !== confirmedPassword ? 'Entered passwords do not match' : '';
};

export const validatePercentageValue = (value: number) => {
  if (value > MAX_PERCENTAGE_OWNED) {
    return MAX_PERCENTAGE_OWNED_ERROR_MESSAGE;
  }

  if (value < MIN_PERCENTAGE_OWNED) {
    return MIN_PERCENTAGE_OWNED_ERROR_MESSAGE;
  }

  return '';
};

export const isValidTaxId = (value: string) =>
  value.length === FEDERAL_TAX_ID_WITH_DASH_MAX_LENGTH && !DEPRECATED_FEDERAL_TAX_IDS.includes(value)
    ? ''
    : 'Please enter a valid Tax Id';

export const isValidEmail = (value: string | null | undefined, fieldName = 'Email', isRequired = true): string => {
  const trimmed = (value ?? '').trim();

  if (isRequired) {
    const requiredMsg = validateRequiredField(trimmed, fieldName);

    if (requiredMsg) {
      return requiredMsg;
    }
  }

  if (/\s/.test(trimmed)) {
    return `${fieldName} must not contain spaces.`;
  }

  if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i.test(trimmed)) {
    return `${fieldName} must be a valid email address.`;
  }

  return '';
};

export const isPercentage = (value: string, fieldName: string): string => {
  const percentValue = parseFloat(value);

  switch (true) {
    case isNaN(percentValue): {
      return `${fieldName} is invalid`;
    }
    case percentValue < 0: {
      return 'Must Not Be Less Than 0%';
    }
    case percentValue > 100: {
      return 'Must Not Be Greater Than 100%';
    }
    default: {
      return '';
    }
  }
};

export const isValidYearsOfBusiness = (input: string): string => {
  if (!!input && +input >= 0 && +input <= 100) {
    return '';
  }

  return 'Allowed range from 0 to 100';
};

export const isValidUrl = (url: string) => {
  return isURL(url) ? '' : 'Please enter a valid website address.';
};
